// Mood Tracker Constants and Utilities

export const MOOD_OPTIONS = [
  { 
    id: 'good', 
    emoji: '😊', 
    label: 'Good', 
    color: '#3DBE80',
    followUpQuestion: "Glad to hear that! What's lifting your mood today?"
  },
  { 
    id: 'okay', 
    emoji: '😐', 
    label: 'Okay', 
    color: '#FFD66B',
    followUpQuestion: "Got it. What's been on your mind?"
  },
  { 
    id: 'low', 
    emoji: '😔', 
    label: 'Low', 
    color: '#667BC6',
    followUpQuestion: "Sorry you're feeling low. What's been tough lately?"
  },
  { 
    id: 'angry', 
    emoji: '😠', 
    label: 'Angry', 
    color: '#E87070',
    followUpQuestion: "That sounds rough. What's been frustrating you?"
  },
  { 
    id: 'anxious', 
    emoji: '😰', 
    label: 'Anxious', 
    color: '#2CA6A4',
    followUpQuestion: "That sounds hard. What's been worrying you?"
  }
];

export const TIME_RANGES = [
  { id: 'week', label: 'Last Week', days: 7 },
  { id: 'month', label: 'Last Month', days: 30 },
  { id: 'quarter', label: 'Last 3 Months', days: 90 },
  { id: 'all', label: 'All Time', days: null }
];

export const MOOD_TRACKER_MESSAGES = {
  initialQuestion: "How are you feeling today?",
  subtext: "Answering this helps your therapist better help you",
  followUpQuestion: "What makes you feel this way?",
  completionMessage: "That's a step towards healing. Your response will help your therapist understand you a bit more.",
  skipText: "Skip",
  completeText: "Complete"
};

// Helper function to format date for API
export const formatDateForAPI = (date) => {
  return date.toISOString().split('T')[0];
};

// Helper function to format date for display
export const formatDateForDisplay = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Helper function to get mood option by ID
export const getMoodOptionById = (moodId) => {
  return MOOD_OPTIONS.find(mood => mood.id === moodId);
};

// Helper function to get time range by ID
export const getTimeRangeById = (rangeId) => {
  return TIME_RANGES.find(range => range.id === rangeId);
};

// Helper function to calculate date range
export const calculateDateRange = (rangeId) => {
  const range = getTimeRangeById(rangeId);
  if (!range) return null;
  
  const endDate = new Date();
  const startDate = range.days ? new Date(endDate.getTime() - (range.days * 24 * 60 * 60 * 1000)) : null;
  
  return { startDate, endDate };
};

// Helper function to validate mood data
export const validateMoodData = (moodData) => {
  if (!moodData.mood || !MOOD_OPTIONS.find(m => m.id === moodData.mood)) {
    throw new Error('Invalid mood selection');
  }
  
  if (moodData.comment && moodData.comment.length > 500) {
    throw new Error('Comment too long (max 500 characters)');
  }
  
  return true;
};

// Helper function to format mood data for API
export const formatMoodDataForAPI = (moodData) => {
  validateMoodData(moodData);
  
  return {
    mood: moodData.mood,
    comment: moodData.comment || '',
    timestamp: new Date().toISOString(),
    date: formatDateForAPI(new Date())
  };
};

// Helper function to format chart data
export const formatChartData = (moodHistory) => {
  if (!moodHistory || !Array.isArray(moodHistory)) {
    return [];
  }
  
  return moodHistory.map(entry => ({
    date: entry.date,
    mood: entry.mood,
    comment: entry.comment,
    timestamp: entry.timestamp,
    displayDate: formatDateForDisplay(entry.date),
    moodOption: getMoodOptionById(entry.mood)
  })).sort((a, b) => new Date(a.date) - new Date(b.date));
};

// Helper function to filter comments by mood
export const filterCommentsByMood = (moodHistory, moodFilter) => {
  if (!moodHistory || !Array.isArray(moodHistory)) {
    return [];
  }
  
  if (!moodFilter || moodFilter === 'all') {
    return moodHistory.filter(entry => entry.comment && entry.comment.trim());
  }
  
  return moodHistory.filter(entry => 
    entry.mood === moodFilter && 
    entry.comment && 
    entry.comment.trim()
  );
};

// Helper function to get mood statistics
export const getMoodStats = (moodHistory) => {
  if (!moodHistory || !Array.isArray(moodHistory)) {
    return {
      totalEntries: 0,
      moodCounts: {},
      averageMood: null,
      mostCommonMood: null
    };
  }
  
  const moodCounts = {};
  let totalEntries = moodHistory.length;
  
  moodHistory.forEach(entry => {
    moodCounts[entry.mood] = (moodCounts[entry.mood] || 0) + 1;
  });
  
  // Calculate average mood (convert to numeric scale)
  const moodScale = { good: 5, okay: 4, low: 3, angry: 2, anxious: 1 };
  const totalScore = moodHistory.reduce((sum, entry) => sum + moodScale[entry.mood], 0);
  const averageMood = totalEntries > 0 ? totalScore / totalEntries : null;
  
  // Find most common mood
  const mostCommonMood = Object.keys(moodCounts).reduce((a, b) => 
    moodCounts[a] > moodCounts[b] ? a : b, 'good'
  );
  
  return {
    totalEntries,
    moodCounts,
    averageMood,
    mostCommonMood
  };
};
